import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import type { ScreenRecord } from '../extension-types.js';
import {
  childEntities,
  fieldOf,
  optionsFor,
  sectionsFor,
  useAppMeta,
  type AppEntity,
  type AppField,
  type AppMeta,
} from '../lib/meta.js';
import { errorIssues, errorSummary, trpc, trpcClient } from '../lib/trpc.js';
import { FieldInput } from './fields.js';
import { Button, cn, ErrorBanner, Label, Table, TD, TH } from './ui.js';

/**
 * The one form every entity uses, for creating and editing. Which fields appear, in which
 * sections, comes from the entity's metadata and its layout; a client's custom fields sit in
 * it like any other. Document lines and payment allocations are edited as rows.
 */

type Values = Record<string, unknown>;
type Row = Record<string, unknown>;

interface RowsInput {
  key: string;
  /** The entity whose fields describe each row: the document's lines, a payment's allocations. */
  entity: AppEntity;
  columns: AppField[];
}

/** Array inputs an entity accepts beside its fields, matched to the child entity each row becomes. */
function rowInputs(meta: AppMeta, entity: AppEntity): RowsInput[] {
  const out: RowsInput[] = [];
  for (const [key, schema] of Object.entries(entity.extra_input)) {
    const properties = Object.keys(
      ((schema as { items?: { properties?: Record<string, unknown> } }).items?.properties ??
        {}) as Record<string, unknown>,
    );
    const candidates = [
      ...(entity.lines ? meta.entities.filter((e) => e.id === entity.lines) : []),
      ...childEntities(meta, entity),
      ...meta.entities.filter((e) => e.parent?.entity === entity.id),
    ];
    const rowEntity = candidates.find(
      (c) => properties.filter((p) => fieldOf(c, p)).length >= Math.min(2, properties.length),
    );
    if (!rowEntity) continue;
    const preferred = [
      'item_id',
      'invoice_id',
      'description',
      'quantity',
      'unit_price',
      'discount_percent',
      'tax_code_id',
      'amount',
    ];
    const columns = properties
      .filter((p) => preferred.includes(p))
      .sort((a, b) => preferred.indexOf(a) - preferred.indexOf(b))
      .map((p) => fieldOf(rowEntity, p))
      .filter((f): f is AppField => Boolean(f));
    out.push({ key, entity: rowEntity, columns });
  }
  return out;
}

function initialValues(
  meta: AppMeta,
  entity: AppEntity,
  record: ScreenRecord | undefined,
  defaults: Values,
): Values {
  if (record) return { ...record };
  const values: Values = {};
  for (const field of entity.fields) {
    if (field.default !== undefined) values[field.key] = field.default;
    if (field.type === 'currency' && field.required)
      values[field.key] = meta.organization.base_currency;
  }
  Object.assign(values, defaults);
  for (const field of entity.fields) {
    if (field.type !== 'select' || values[field.key] !== undefined || !field.required) continue;
    const options = optionsFor(meta, field, values);
    const first = options.find((o) => o.default) ?? options[0];
    if (first) values[field.key] = first.value;
  }
  return values;
}

function rowsFrom(record: ScreenRecord | undefined, input: RowsInput): Row[] {
  const existing = record?.[input.key];
  if (!Array.isArray(existing)) return [];
  return existing.map((row) =>
    Object.fromEntries(input.columns.map((c) => [c.key, (row as Row)[c.key] ?? null])),
  );
}

export function RecordForm({
  entity,
  record,
  defaults = {},
  onSaved,
  onCancel,
  submitLabel,
}: {
  entity: AppEntity;
  record?: ScreenRecord;
  defaults?: Values;
  onSaved: (record: ScreenRecord) => void;
  onCancel?: () => void;
  submitLabel?: string;
}) {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const editing = Boolean(record);
  const [values, setValues] = useState<Values>(() => initialValues(meta, entity, record, defaults));
  const inputs = useMemo(() => rowInputs(meta, entity), [meta, entity]);
  const [rows, setRows] = useState<Record<string, Row[]>>(() =>
    Object.fromEntries(inputs.map((i) => [i.key, rowsFrom(record, i)])),
  );
  const locked = Boolean(
    record && typeof record['state'] === 'string' && entity.states[record['state']]?.locked,
  );

  const sections = useMemo(() => {
    const writable = (f: AppField) => f.writable && !(editing && f.immutable);
    const laidOut = sectionsFor(meta, entity).map((s) => ({
      label: s.label,
      fields: s.fields.filter(writable),
    }));
    // A required field the layout leaves out still has to be filled in to create a record.
    const placed = new Set(laidOut.flatMap((s) => s.fields.map((f) => f.key)));
    const missing = editing
      ? []
      : entity.fields.filter(
          (f) => f.required && writable(f) && !placed.has(f.key) && defaults[f.key] === undefined,
        );
    if (missing.length > 0 && laidOut[0]) laidOut[0].fields.unshift(...missing);
    return laidOut.filter((s) => s.fields.length > 0);
  }, [meta, entity, editing, defaults]);

  const save = useMutation({
    mutationFn: async () => {
      const data: Values = {};
      const all = sections.flatMap((s) => s.fields);
      for (const field of all) {
        const value = values[field.key];
        const empty = value === '' || value === undefined;
        if (editing) {
          const before = record?.[field.key] ?? null;
          const after = empty ? null : value;
          if (JSON.stringify(before) !== JSON.stringify(after)) data[field.key] = after;
        } else if (!empty && value !== null) {
          data[field.key] = value;
        }
      }
      for (const [key, value] of Object.entries(defaults))
        if (!(key in data) && !editing) data[key] = value;
      for (const input of inputs) {
        const list = (rows[input.key] ?? []).map((row) =>
          Object.fromEntries(
            Object.entries(row).filter(([, v]) => v !== '' && v !== null && v !== undefined),
          ),
        );
        const changed = JSON.stringify(list) !== JSON.stringify(rowsFrom(record, input));
        if (!editing ? list.length > 0 : changed) data[input.key] = list;
      }
      if (record) {
        if (Object.keys(data).length === 0) return record;
        const expected =
          typeof record['updated_at'] === 'string'
            ? { expected_updated_at: record['updated_at'] }
            : {};
        return (await trpcClient.records.update.mutate({
          entity: entity.id,
          id: record.id,
          data,
          ...expected,
        })) as ScreenRecord;
      }
      return (await trpcClient.records.create.mutate({ entity: entity.id, data })) as ScreenRecord;
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: trpc.records.pathKey() });
      onSaved(saved);
    },
  });

  const issues = errorIssues(save.error);
  const issueFor = (path: string) =>
    issues.find(
      (i) => i.path === path || i.path.startsWith(`${path}.`) || i.path.startsWith(`${path}[`),
    )?.message;
  const unplaced = issues.filter(
    (i) =>
      !sections.some((s) => s.fields.some((f) => i.path === f.key)) &&
      !inputs.some((r) => i.path.startsWith(r.key)),
  );

  const set = (field: AppField, value: unknown) =>
    setValues((current) => {
      const next = { ...current, [field.key]: value };
      // Options that depend on this field (a pipeline's stages) start again from the first.
      for (const other of entity.fields) {
        if (other.options_from?.depends_on === field.key)
          next[other.key] = optionsFor(meta, other, next)[0]?.value ?? null;
      }
      return next;
    });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <form onSubmit={submit} className="space-y-6" noValidate>
      {save.error && (
        <ErrorBanner title={errorSummary(save.error)}>
          {unplaced.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {unplaced.map((i) => (
                <li key={`${i.path}:${i.message}`}>
                  {i.path ? `${i.path}: ` : ''}
                  {i.message}
                </li>
              ))}
            </ul>
          )}
        </ErrorBanner>
      )}
      {locked && (
        <ErrorBanner>
          This record is {entity.states[String(record!['state'])]?.label.toLowerCase()}: its fields
          can no longer be edited.
        </ErrorBanner>
      )}
      {sections.map((section) => (
        <fieldset key={section.label} disabled={locked} className="min-w-0">
          <legend className="mb-3 text-sm font-semibold text-slate-800">{section.label}</legend>
          <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
            {section.fields.map((field) => {
              const id = `field-${field.key}`;
              const problem = issueFor(field.key);
              const wide = field.type === 'long_text' || field.type === 'multi_select';
              return (
                <div key={field.key} className={cn(wide && 'sm:col-span-2')}>
                  <Label htmlFor={id} required={Boolean(field.required)}>
                    {field.label}
                  </Label>
                  <FieldInput
                    field={field}
                    id={id}
                    value={values[field.key]}
                    record={values}
                    onChange={(v) => set(field, v)}
                    invalid={Boolean(problem)}
                  />
                  {problem ? (
                    <p className="mt-1 text-xs text-red-600">{problem}</p>
                  ) : (
                    field.help && <p className="mt-1 text-xs text-slate-500">{field.help}</p>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
      ))}
      {inputs.map((input) => (
        <RowsEditor
          key={input.key}
          input={input}
          rows={rows[input.key] ?? []}
          disabled={locked}
          problem={issueFor(input.key)}
          onChange={(next) => setRows((current) => ({ ...current, [input.key]: next }))}
        />
      ))}
      <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="primary" busy={save.isPending} disabled={locked}>
          {submitLabel ?? (editing ? 'Save changes' : `Create ${entity.label.toLowerCase()}`)}
        </Button>
      </div>
    </form>
  );
}

function RowsEditor({
  input,
  rows,
  onChange,
  disabled,
  problem,
}: {
  input: RowsInput;
  rows: Row[];
  onChange: (rows: Row[]) => void;
  disabled: boolean;
  problem: string | undefined;
}) {
  const blank = () =>
    Object.fromEntries(input.columns.map((c) => [c.key, c.key === 'quantity' ? '1' : null]));
  const update = (index: number, key: string, value: unknown) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  return (
    <fieldset disabled={disabled} className="min-w-0">
      <legend className="mb-3 text-sm font-semibold text-slate-800">
        {input.entity.plural.replace(/^(Quote|Invoice|Sales order|Payment) /i, '')}
      </legend>
      {/* Too narrow for the editor as a table (a phone), each row is a card of inputs. */}
      <div className="rounded-md border border-slate-200">
        <Table stack className="min-w-[720px]">
          <thead>
            <tr>
              {input.columns.map((c) => (
                <TH
                  key={c.key}
                  align={
                    ['quantity', 'unit_price', 'discount_percent', 'amount'].includes(c.key)
                      ? 'right'
                      : 'left'
                  }
                >
                  {c.label}
                </TH>
              ))}
              <TH>
                <span className="sr-only">Remove</span>
              </TH>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <TD primary className="text-slate-500">
                  <span>No rows yet.</span>
                </TD>
              </tr>
            )}
            {rows.map((row, index) => (
              <tr key={index}>
                {input.columns.map((c) => (
                  <TD
                    key={c.key}
                    label={c.label}
                    className={cn(
                      c.type === 'reference'
                        ? 'min-w-52'
                        : c.key === 'description'
                          ? 'min-w-56'
                          : 'w-28',
                    )}
                  >
                    {/* The heading names the column on a desk; on a phone it is hidden, so
                        each input carries its own name. */}
                    <label htmlFor={`${input.key}-${index}-${c.key}`} className="sr-only">
                      {c.label}, row {index + 1}
                    </label>
                    <FieldInput
                      field={c}
                      value={row[c.key]}
                      record={row}
                      onChange={(v) => update(index, c.key, v)}
                      id={`${input.key}-${index}-${c.key}`}
                    />
                  </TD>
                ))}
                <TD className="w-10">
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label="Remove row"
                    onClick={() => onChange(rows.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TD>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      {problem && <p className="mt-1 text-xs text-red-600">{problem}</p>}
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        onClick={() => onChange([...rows, blank()])}
      >
        <Plus className="size-4" /> Add row
      </Button>
    </fieldset>
  );
}
